import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";

type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
  description?: string;
};

type Props = {
  value?: string;
  onChange: (value: string) => void;
  options?: Option[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  isSearchable?: boolean;
};

export const GenericSelect: React.FC<Props> = ({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  className,
  isSearchable = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const selected = React.useMemo(
    () => options.find((opt) => String(opt.value) === String(value)),
    [options, value],
  );

  const filteredOptions = React.useMemo(() => {
    if (!isSearchable || !searchTerm) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, isSearchable]);



  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearchTerm("");
      }}
      modal={false}
    >
      <PopoverTrigger asChild>
        <div
          ref={dropdownRef}
          className={cn(
            "flex w-full items-center justify-between h-11 px-4 rounded-lg font-normal transition-all duration-200 cursor-pointer border",
            "border-gray-300 bg-white hover:border-blue-400 focus-within:border-blue-500",
            open ? "border-blue-500 ring-2 ring-blue-500/10" : "",
            disabled ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed" : "text-gray-900",
            className,
          )}
          onClick={(e) => {
            if (!disabled) {
              setOpen(!open);
            }
          }}
        >
          {isSearchable && options.length > 5 ? (
            <input
              type="text"
              value={open ? searchTerm : selected?.label || ""}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => {
                // Do not auto-open on focus to prevent opening on modal mount or tab navigation
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled && !open) setOpen(true);
              }}
              onKeyDown={(e) => {
                if ((e.key === "ArrowDown" || e.key === "Enter") && !open && !disabled) {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
              placeholder={selected?.label || placeholder || "Select..."}
              className={cn(
                "flex-1 min-w-0 bg-transparent outline-none truncate text-sm cursor-pointer",
                !selected && !searchTerm ? "placeholder:text-gray-400 font-normal" : "text-gray-900 font-medium"
              )}
              disabled={disabled}
            />
          ) : (
            <span
              className={cn(
                "truncate flex-1 min-w-0 text-sm",
                !selected ? "text-gray-400 font-normal" : "text-gray-900 font-medium"
              )}
            >
              {selected?.label || placeholder || "Select..."}
            </span>
          )}
          <ChevronDown
            className={cn(
              "ml-2 h-4 w-4 shrink-0 transition-transform duration-200 opacity-50",
              open ? "rotate-180" : "",
            )}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 border-none bg-transparent shadow-none z-[9999]"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div
          className={cn(
            "bg-white border border-gray-100 rounded-xl shadow-2xl",
            "max-h-[300px] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200",
          )}
          data-radix-scroll-lock-ignore
          onWheel={(e) => e.stopPropagation()}
        >
          <div
            className="py-1 overflow-y-auto max-h-[300px] p-1"
            data-radix-scroll-lock-ignore
            onWheel={(e) => e.stopPropagation()}
          >
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500 font-medium">
                No results found.
              </div>
            ) : (
              <>
                <div
                  onClick={() => {
                    onChange("");
                    setOpen(false);
                    setSearchTerm("");
                  }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 my-0.5 text-sm rounded-md cursor-pointer transition-colors",
                    "text-gray-500 hover:bg-blue-600 hover:text-white",
                  )}
                >
                  <div className="flex-1 truncate">{placeholder || "Select..."}</div>
                  <Check
                    className={cn(
                      "h-4 w-4",
                      !value || value === "" ? "opacity-100" : "opacity-0",
                    )}
                  />
                </div>

                {filteredOptions.map((option) => {
                  const optionValue = String(option.value);
                  const isSelected = String(value) === optionValue;

                  return (
                    <div
                      key={optionValue}
                      onClick={() => {
                        if (!option.disabled) {
                          onChange(optionValue);
                          setOpen(false);
                          setSearchTerm("");
                        }
                      }}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 my-0.5 text-sm rounded-md cursor-pointer transition-all duration-200",
                        isSelected
                          ? "bg-blue-600 text-white font-medium"
                          : "text-gray-700 hover:bg-blue-500 hover:text-white",
                        option.disabled ? "opacity-50 cursor-not-allowed" : "",
                      )}
                    >
                      <div className="flex-1 truncate">{option.label}</div>
                      <Check
                        className={cn(
                          "h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
